import { TM_PENDING_PAYOUTS, TM_REPORT_TABLES } from './constants.js';

/** Normalise for comparison only; API payload uses exact Description from the row. */
export function normaliseFieldName(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

/**
 * Client-side exclusions that have a separate UX and must never show the eye icon,
 * even if the backend list ever includes them.
 */
const CLIENT_EXCLUDED = new Set([
  normaliseFieldName(TM_PENDING_PAYOUTS.REPORT_ROW_LABEL).toLowerCase(),
]);

/** UI report table title → API reportTable key used for dateScope / variant selection. */
const REPORT_TABLE_API_KEY = {
  [TM_REPORT_TABLES.BANK_BALANCE_MOVEMENT]: 'bbm',
  [TM_REPORT_TABLES.CLOSING_BALANCE_SUMMARY]: 'cbs',
};

/**
 * Map report UI table name to API `reportTable` (`bbm` | `cbs`).
 * Required for shared labels (Nil Debit, NEFT Return) so backend can pick the
 * correct dateScope variant (bbm → report_day, cbs → cumulative).
 * @param {string} tableName - e.g. "Bank Balance Movement"
 * @returns {'bbm'|'cbs'|null}
 */
export function resolveReportTableKey(tableName) {
  return REPORT_TABLE_API_KEY[tableName] || null;
}

/**
 * Extract field names from form-data drillDownFields.
 * Accepts string[] or `{ field }[]` (reportTable on config/objects is ignored for eligibility).
 * @param {Array<string|{field?: string, name?: string}>} drillDownFields
 * @returns {string[]}
 */
export function extractDrillDownFieldNames(drillDownFields = []) {
  if (!Array.isArray(drillDownFields)) return [];

  const names = [];
  const seen = new Set();

  for (const item of drillDownFields) {
    const raw = typeof item === 'string'
      ? item
      : (item?.field || item?.name || '');
    const field = normaliseFieldName(raw);
    if (!field) continue;
    const key = field.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(field);
  }

  return names;
}

/**
 * Whether a report row Description is eligible for drill-down (by field name only).
 * Aliases are included in drillDownFields from config; matching is case-insensitive.
 * @param {string} description
 * @param {Array<string|{field?: string}>} drillDownFields
 * @returns {boolean}
 */
export function isDrilldownEligible(description, drillDownFields = []) {
  const normalised = normaliseFieldName(description).toLowerCase();
  if (!normalised || CLIENT_EXCLUDED.has(normalised)) return false;

  const allowed = new Set(
    extractDrillDownFieldNames(drillDownFields).map((f) => f.toLowerCase())
  );
  return allowed.has(normalised);
}
